import { useState } from 'react';
import {
  useExpenses, useApproveExpense, useDenyExpense,
  useForemanApproveExpense, useForemanDenyExpense,
} from '../../hooks/useExpenses.js';
import { useAuth } from '../../hooks/useAuth.js';
import { useError } from '../../components/shared/ErrorBanner.jsx';
import { centsToUsd } from '../../lib/format.js';

// Admin/bookkeeper view: every expense across crew with filter by status.
// Pending queue at top; deeper history below by status filter.
export default function ExpensesAdminPage() {
  const [filter, setFilter] = useState('submitted');
  const { data: expenses = [], isLoading } = useExpenses({ target: 'all' });

  const filtered = expenses.filter(e => filter === 'all' || e.status === filter);

  return (
    <div>
      <div className="range-bar">
        <select className="field-select" value={filter} onChange={e => setFilter(e.target.value)}>
          <option value="submitted">Pending</option>
          <option value="approved">Approved</option>
          <option value="denied">Denied</option>
          <option value="reimbursed">Reimbursed</option>
          <option value="all">All</option>
        </select>
        <span style={{ color: 'var(--warm-gray)', fontSize: 11 }}>{filtered.length}</span>
      </div>
      {isLoading ? (
        <div className="loading">Loading…</div>
      ) : filtered.length === 0 ? (
        <div className="loading">Nothing here.</div>
      ) : (
        <table className="tbl">
          <thead>
            <tr>
              <th>Crew</th>
              <th>Date</th>
              <th>Kind</th>
              <th className="num">Amount</th>
              <th>Notes</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {filtered.map(e => <AdminRow key={e.id} e={e} />)}
          </tbody>
        </table>
      )}
    </div>
  );
}

function AdminRow({ e }) {
  const { isAdmin, isBookkeeper } = useAuth();
  // Admin + bookkeeper → admin endpoint (existing). Foreman-only →
  // foreman endpoint (canViewUser-scoped). Keeps the bookkeeper's
  // financial surface intact.
  const useForemanPath = !isAdmin && !isBookkeeper;
  const adminApprove   = useApproveExpense();
  const adminDeny      = useDenyExpense();
  const foremanApprove = useForemanApproveExpense();
  const foremanDeny    = useForemanDenyExpense();
  const approve = useForemanPath ? foremanApprove : adminApprove;
  const deny    = useForemanPath ? foremanDeny    : adminDeny;
  const { show } = useError();

  const onApprove = async () => {
    try { await approve.mutateAsync(e.id); } catch (err) { show(err.message); }
  };
  const onDeny = async () => {
    const reason = prompt('Deny this expense — reason?');
    if (!reason) return;
    try { await deny.mutateAsync({ id: e.id, reason }); } catch (err) { show(err.message); }
  };
  const busy = approve.isPending || deny.isPending;

  return (
    <tr>
      <td>{e.user_name || e.user_id}</td>
      <td>{new Date(e.date).toLocaleDateString()}</td>
      <td><span className="badge">{e.kind}</span></td>
      <td className="num">{centsToUsd(e.amount_cents)}</td>
      <td style={{ maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis' }}>
        {e.miles ? `${e.miles} mi · ` : ''}{e.notes || ''}
      </td>
      <td style={{ textAlign: 'right' }}>
        {e.status === 'submitted' ? (
          <>
            <button className="btn-sm primary" onClick={onApprove} disabled={busy}>Approve</button>{' '}
            <button className="btn-sm" onClick={onDeny} disabled={busy}>Deny</button>
          </>
        ) : (
          <span className={'badge' + (e.status === 'approved' || e.status === 'reimbursed' ? ' orange' : '')}>{e.status}</span>
        )}
      </td>
    </tr>
  );
}
